import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import api, { productionAPI } from '@/lib/api';
import PrintButton from '@/components/PrintButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Factory, FileUp, Loader2, Printer, Truck, Plus, ChevronLeft, ChevronRight, Trash2, CalendarPlus, Lock, LockOpen, GripVertical, Euro, Pencil, CalendarDays, Settings2, ArchiveRestore, Tags } from 'lucide-react';
import LabelsView from '@/pages/production/LabelsView';
import { useAuth } from '@/context/AuthContext';
import PrintWeekView from '@/pages/production/PrintWeekView';
import ManageClientsDialog from '@/pages/production/ManageClientsDialog';
import BlView from '@/pages/production/BlView';
import DayCalendar from '@/pages/production/DayCalendar';

const MONTHS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAY_LETTER = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const todayIso = () => new Date().toISOString().slice(0, 10);

async function openPdf(url, errMsg) {
    try {
        const res = await api.get(url, { responseType: 'blob' });
        window.open(URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), '_blank');
    } catch (e) {
        let detail = errMsg;
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch { /* ignore */ }
        toast.error(detail || errMsg);
    }
}

export default function ProductionPage() {
    const { user } = useAuth();
    const restricted = user && !['eeginer', 'ddultheo'].includes(user.username) && !(user.permissions || []).includes('production_view');
    const canManageClients = user && (user.role === 'admin' || ['eeginer', 'ddultheo'].includes(user.username) || (user.permissions || []).includes('production_edit'));
    const [manageClientsOpen, setManageClientsOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [products, setProducts] = useState([]);
    const [activeClient, setActiveClient] = useState(null);
    const [month, setMonth] = useState(todayIso().slice(0, 7));
    const [days, setDays] = useState({});
    const [loading, setLoading] = useState(true);
    const [importing, setImporting] = useState(false);
    const [editing, setEditing] = useState(null); // {date, productId, value}
    const [pending, setPending] = useState(null); // {date, productId, newQty, oldQty}
    const [pdfDate, setPdfDate] = useState(todayIso());
    const [addProductOpen, setAddProductOpen] = useState(false);
    const [nextMonthOpen, setNextMonthOpen] = useState(false);
    const [deleteMonthOpen, setDeleteMonthOpen] = useState(false);
    const [deletingMonth, setDeletingMonth] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [trashOpen, setTrashOpen] = useState(false);
    const [backups, setBackups] = useState([]);
    const [trashLoading, setTrashLoading] = useState(false);
    const [restoringId, setRestoringId] = useState(null);
    const [creatingMonth, setCreatingMonth] = useState(false);
    const [monthLocked, setMonthLocked] = useState(false);
    const [canCopyNext, setCanCopyNext] = useState(false);
    const [gridProductIds, setGridProductIds] = useState([]);
    const [sheetView, setSheetView] = useState(false);
    const [billingView, setBillingView] = useState(false);
    const [printView, setPrintView] = useState(false);
    const [blPreviewView, setBlPreviewView] = useState(false);
    const [labelsView, setLabelsView] = useState(false);
    const [clearCorr, setClearCorr] = useState(null); // {date, productId}
    const [dragPid, setDragPid] = useState(null);
    const [dragOverPid, setDragOverPid] = useState(null);
    const [sel, setSel] = useState(null); // {anchor:{r,c}, focus:{r,c}}
    const selDragRef = useRef(false);
    const clipRef = useRef('');
    const gridWrapRef = useRef(null);
    const fileRef = useRef(null);

    const client = clients.find(c => c.id === activeClient);
    const productMap = useMemo(() => Object.fromEntries(products.map(p => [p.id, p])), [products]);

    const monthDays = useMemo(() => {
        const [y, m] = month.split('-').map(Number);
        const n = new Date(y, m, 0).getDate();
        return Array.from({ length: n }, (_, i) => {
            const iso = `${month}-${String(i + 1).padStart(2, '0')}`;
            return { iso, num: i + 1, letter: DAY_LETTER[new Date(iso + 'T00:00:00').getDay()] };
        });
    }, [month]);

    const loadBase = useCallback(async () => {
        const [c, p] = await Promise.all([productionAPI.clients(), productionAPI.products()]);
        setClients(c.data);
        setProducts(p.data);
        if (!activeClient && c.data.length) setActiveClient(c.data[0].id);
        // eslint-disable-next-line
    }, [activeClient]);

    const loadGrid = useCallback(async () => {
        if (!activeClient) return;
        const res = await productionAPI.grid({ client_id: activeClient, month });
        setDays(Object.fromEntries(res.data.days.map(d => [d.date, d])));
        setMonthLocked(!!res.data.locked);
        setCanCopyNext(!!res.data.month_has_data && !res.data.next_month_has_data);
        setGridProductIds(res.data.product_ids || []);
        setLoading(false);
    }, [activeClient, month]);

    useEffect(() => { if (!restricted) loadBase(); }, [loadBase, restricted]);
    useEffect(() => { if (!restricted) loadGrid(); }, [loadGrid, restricted]);

    const shiftMonth = (delta) => {
        const [y, m] = month.split('-').map(Number);
        const d = new Date(y, m - 1 + delta, 1);
        setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };

    const nextMonthLabel = useMemo(() => {
        const [y, m] = month.split('-').map(Number);
        const d = new Date(y, m, 1);
        return `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
    }, [month]);

    const createNextMonth = async () => {
        setCreatingMonth(true);
        try {
            const res = await productionAPI.createNextMonth(month);
            const { target_month, created_days, sequences } = res.data;
            const seq = Object.entries(sequences || {}).map(([code, s]) => `${code} BL ${s.from}-${s.to}`).join(' · ');
            toast.success(`${created_days} jour(s) créé(s) pour ${target_month}${seq ? ` — ${seq}` : ''}`, { duration: 8000 });
            setNextMonthOpen(false);
            setMonth(target_month);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création du mois');
        }
        setCreatingMonth(false);
    };

    const monthLabel = `${MONTHS_FR[Number(month.split('-')[1]) - 1]} ${month.split('-')[0]}`;
    const deleteConfirmOk = deleteConfirmText.trim().toLowerCase() === monthLabel.toLowerCase();

    const deleteMonth = async () => {
        if (!deleteConfirmOk) return;
        setDeletingMonth(true);
        try {
            const res = await productionAPI.deleteMonth(month);
            toast.success(`${res.data.deleted_days} jour(s) effacé(s) sur ${monthLabel} — sauvegarde conservée 30 jours dans la corbeille`);
            setDeleteMonthOpen(false);
            loadGrid();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l\'effacement du mois');
        }
        setDeletingMonth(false);
    };

    const loadBackups = async () => {
        setTrashLoading(true);
        try {
            const res = await productionAPI.listBackups();
            setBackups(res.data);
        } catch { /* ignore */ }
        setTrashLoading(false);
    };

    const restoreBackup = async (id) => {
        setRestoringId(id);
        try {
            const res = await productionAPI.restoreBackup(id);
            toast.success(`${res.data.restored_days} jour(s) restauré(s) sur ${res.data.month}`);
            setMonth(res.data.month);
            setTrashOpen(false);
            loadGrid();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la restauration');
        }
        setRestoringId(null);
    };

    const purgeBackup = async (id) => {
        try {
            await productionAPI.deleteBackup(id);
            setBackups(b => b.filter(x => x.id !== id));
            toast.success('Sauvegarde supprimée définitivement');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
    };

    const toggleLock = async () => {
        const locked = !monthLocked;
        await productionAPI.setMonthLock({ month, locked });
        setMonthLocked(locked);
        toast.success(locked ? `${MONTHS_FR[Number(month.split('-')[1]) - 1]} verrouillé` : `${MONTHS_FR[Number(month.split('-')[1]) - 1]} déverrouillé`);
    };

    const guardLocked = () => {
        if (monthLocked) { toast.info('Mois verrouillé — déverrouillez-le pour modifier'); return true; }
        return false;
    };

    const moveRow = async (fromPid, toPid) => {
        if (!fromPid || !toPid || fromPid === toPid) return;
        const ids = [...gridProductIds];
        const fi = ids.indexOf(fromPid), ti = ids.indexOf(toPid);
        if (fi < 0 || ti < 0) return;
        ids.splice(fi, 1);
        ids.splice(ids.indexOf(toPid) + (fi < ti ? 1 : 0), 0, fromPid);
        setGridProductIds(ids);
        await productionAPI.setClientProducts(activeClient, ids, month);
        toast.success('Ordre des lignes mis à jour (grille + BL)');
    };

    const removeCorrection = async () => {
        if (!clearCorr) return;
        try {
            const res = await productionAPI.clearCorrection({ client_id: activeClient, date: clearCorr.date, product_id: clearCorr.productId });
            setDays(p => ({ ...p, [clearCorr.date]: res.data }));
            toast.success('Marquage rouge retiré');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        }
        setClearCorr(null);
    };

    const saveCell = async (date, productId, qty, visibleCorrection = false) => {
        const res = await productionAPI.setCell({ client_id: activeClient, date, product_id: productId, qty, visible_correction: visibleCorrection });
        setDays(p => ({ ...p, [date]: res.data }));
    };

    const commitEdit = () => {
        if (!editing) return;
        const { date, productId, value } = editing;
        setEditing(null);
        const raw = String(value).trim().replace(',', '.');
        const qty = raw === '' ? null : Number(raw);
        if (raw !== '' && isNaN(qty)) { toast.error('Valeur invalide'); return; }
        const old = days[date]?.quantities?.[productId];
        if (old === qty || (old === undefined && qty === null)) return;
        if (old !== undefined && old !== null) {
            setPending({ date, productId, newQty: qty, oldQty: old });
        } else {
            saveCell(date, productId, qty);
        }
    };

    const importFiles = async (files) => {
        if (!files.length) return;
        setImporting(true);
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append('file', f);
                const res = await productionAPI.importExcel(fd);
                toast.success(`${f.name} : ${res.data.created_days} jour(s) importé(s), ${res.data.skipped_days} déjà présent(s)`);
            } catch (e) {
                toast.error(`${f.name} : ${e.response?.data?.detail || 'erreur'}`);
            }
        }
        setImporting(false);
        await loadBase();
        await loadGrid();
    };

    const rowIds = useMemo(() => gridProductIds.filter(id => productMap[id]), [gridProductIds, productMap]);

    useEffect(() => {
        const up = () => { selDragRef.current = false; };
        window.addEventListener('mouseup', up);
        return () => window.removeEventListener('mouseup', up);
    }, []);

    useEffect(() => { setSel(null); }, [activeClient, month]);

    const selBounds = sel ? {
        r1: Math.min(sel.anchor.r, sel.focus.r), r2: Math.max(sel.anchor.r, sel.focus.r),
        c1: Math.min(sel.anchor.c, sel.focus.c), c2: Math.max(sel.anchor.c, sel.focus.c),
    } : null;
    const inSel = (r, c) => selBounds && r >= selBounds.r1 && r <= selBounds.r2 && c >= selBounds.c1 && c <= selBounds.c2;

    const copySel = () => {
        if (!selBounds) return;
        const lines = [];
        for (let r = selBounds.r1; r <= selBounds.r2; r++) {
            const cols = [];
            for (let c = selBounds.c1; c <= selBounds.c2; c++) {
                const q = days[monthDays[c].iso]?.quantities?.[rowIds[r]];
                cols.push(q !== undefined && q !== null ? q : '');
            }
            lines.push(cols.join('\t'));
        }
        const tsv = lines.join('\n');
        clipRef.current = tsv;
        navigator.clipboard?.writeText(tsv).catch(() => {});
        toast.success(`${(selBounds.r2 - selBounds.r1 + 1) * (selBounds.c2 - selBounds.c1 + 1)} cellule(s) copiée(s)`);
    };

    const pasteSel = async (text) => {
        if (guardLocked()) return;
        if (!selBounds) { toast.info('Sélectionnez d\'abord la cellule de destination'); return; }
        const tsv = (text || clipRef.current || '').replace(/\r/g, '');
        if (!tsv) { toast.info('Rien à coller'); return; }
        const updates = [];
        tsv.split('\n').forEach((line, dr) => {
            line.split('\t').forEach((val, dc) => {
                const r = selBounds.r1 + dr, c = selBounds.c1 + dc;
                if (r >= rowIds.length || c >= monthDays.length) return;
                const raw = val.trim().replace(',', '.');
                const qty = raw === '' ? null : Number(raw);
                if (raw !== '' && isNaN(qty)) return;
                updates.push({ date: monthDays[c].iso, product_id: rowIds[r], qty });
            });
        });
        if (!updates.length) return;
        await productionAPI.setCells({ client_id: activeClient, updates });
        await loadGrid();
        toast.success(`${updates.length} cellule(s) collée(s)`);
    };

    const onGridKeyDown = (e) => {
        if (editing) return;
        const mod = e.ctrlKey || e.metaKey;
        if (mod && e.key.toLowerCase() === 'c') { e.preventDefault(); copySel(); }
        else if (mod && e.key.toLowerCase() === 'v') {
            e.preventDefault();
            (navigator.clipboard?.readText ? navigator.clipboard.readText() : Promise.reject())
                .then(t => pasteSel(t)).catch(() => pasteSel());
        }
        else if (e.key === 'Enter' && selBounds) {
            e.preventDefault();
            if (guardLocked()) return;
            const pid = rowIds[selBounds.r1], iso = monthDays[selBounds.c1].iso;
            setEditing({ date: iso, productId: pid, value: days[iso]?.quantities?.[pid] ?? '' });
        }
        else if ((e.key === 'Delete' || e.key === 'Backspace') && selBounds) {
            e.preventDefault();
            if (guardLocked()) return;
            const updates = [];
            for (let r = selBounds.r1; r <= selBounds.r2; r++)
                for (let c = selBounds.c1; c <= selBounds.c2; c++)
                    if (days[monthDays[c].iso]?.quantities?.[rowIds[r]] !== undefined)
                        updates.push({ date: monthDays[c].iso, product_id: rowIds[r], qty: null });
            if (updates.length) productionAPI.setCells({ client_id: activeClient, updates }).then(loadGrid);
        }
        else if (e.key === 'Escape') setSel(null);
    };

    if (restricted) return (
        <Card><CardContent className="py-14 text-center text-slate-400" data-testid="production-restricted">
            <Lock className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            Accès réservé.
        </CardContent></Card>
    );
    if (loading && !clients.length) return <div className="py-12 text-center text-slate-400">Chargement…</div>;

    return (
        <div className="space-y-4 animate-fade-in" data-testid="production-page">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <Factory className="w-6 h-6 text-teal-600" />
                    <h1 className="text-2xl font-bold text-slate-900">Production Concept Lab</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Import Excel masqué à la demande (2026-10) — réactivable en remettant la condition à true */}
                    {false && (
                        <>
                            <input ref={fileRef} type="file" accept=".xlsx" multiple className="hidden"
                                onChange={e => { importFiles(Array.from(e.target.files || [])); e.target.value = ''; }}
                                data-testid="production-import-input" />
                            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="production-import-btn">
                                {importing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileUp className="w-4 h-4 mr-1.5" />}
                                Importer Excel
                            </Button>
                        </>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1 flex-wrap">
                    {clients.map(c => (
                        <button key={c.id} onClick={() => { setActiveClient(c.id); setSheetView(false); setBillingView(false); setPrintView(false); setBlPreviewView(false); setLabelsView(false); }}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${activeClient === c.id && !sheetView && !billingView && !printView && !blPreviewView && !labelsView ? 'bg-slate-900 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                            data-testid={`production-client-${c.code}`}>
                            {c.code}
                        </button>
                    ))}
                    {canManageClients && (
                        <button onClick={() => setManageClientsOpen(true)}
                            className="p-1.5 rounded-full border bg-white text-slate-400 hover:text-slate-700 hover:border-slate-400 transition-colors"
                            title="Gérer les clients" data-testid="manage-clients-btn">
                            <Settings2 className="w-4 h-4" />
                        </button>
                    )}
                    <button onClick={() => { setPdfDate(month === todayIso().slice(0, 7) ? todayIso() : `${month}-01`); setSheetView(true); setBillingView(false); setPrintView(false); setBlPreviewView(false); }}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${sheetView ? 'bg-teal-700 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                        data-testid="production-client-FICHE">
                        <Printer className="w-3.5 h-3.5" />Fiche
                    </button>
                    <button onClick={() => { setPdfDate(month === todayIso().slice(0, 7) ? todayIso() : `${month}-01`); setBlPreviewView(true); setSheetView(false); setBillingView(false); setPrintView(false); }}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${blPreviewView ? 'bg-teal-700 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                        data-testid="production-client-BL">
                        <Truck className="w-3.5 h-3.5" />Bon de Livraison
                    </button>
                    {(user?.role === 'admin' || (user?.permissions || []).includes('production_billing_view')) && (
                        <button onClick={() => { setBillingView(true); setSheetView(false); setPrintView(false); setBlPreviewView(false); }}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${billingView ? 'bg-emerald-700 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                            data-testid="production-client-FACTURATION">
                            <Euro className="w-3.5 h-3.5" />Facturation
                        </button>
                    )}
                    <button onClick={() => { setPrintView(true); setSheetView(false); setBillingView(false); setBlPreviewView(false); setLabelsView(false); }}
                        className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${printView ? 'bg-sky-700 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                        data-testid="production-client-PRODUCTION">
                        <CalendarDays className="w-3.5 h-3.5" />Impressions
                    </button>
                    {(user?.role === 'admin' || (user?.permissions || []).includes('labels_view')) && (
                        <button onClick={() => { setLabelsView(true); setPrintView(false); setSheetView(false); setBillingView(false); setBlPreviewView(false); }}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${labelsView ? 'bg-violet-700 text-white' : 'bg-white border text-slate-600 hover:text-slate-900'}`}
                            data-testid="production-client-ETIQUETTES">
                            <Tags className="w-3.5 h-3.5" />Étiquettes
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-1 ml-auto flex-wrap">
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftMonth(-1)} data-testid="production-prev-month"><ChevronLeft className="w-4 h-4" /></Button>
                    <span className="font-semibold text-sm w-36 text-center" data-testid="production-month-label">
                        {MONTHS_FR[Number(month.split('-')[1]) - 1]} {month.split('-')[0]}
                    </span>
                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => shiftMonth(1)} data-testid="production-next-month"><ChevronRight className="w-4 h-4" /></Button>
                    <Button variant={monthLocked ? 'default' : 'outline'} size="sm"
                        className={`h-8 w-8 p-0 ${monthLocked ? 'bg-red-600 hover:bg-red-700' : ''}`}
                        title={monthLocked ? 'Mois verrouillé — cliquer pour déverrouiller' : 'Verrouiller ce mois'}
                        onClick={toggleLock} data-testid="production-lock-btn">
                        {monthLocked ? <Lock className="w-4 h-4" /> : <LockOpen className="w-4 h-4" />}
                    </Button>
                    {canCopyNext && (
                        <Button variant="outline" size="sm" className="h-8" onClick={() => setNextMonthOpen(true)} data-testid="production-create-next-month-btn">
                            <CalendarPlus className="w-4 h-4 mr-1" />Recopier données sur {nextMonthLabel.split(' ')[0]}
                        </Button>
                    )}
                    {!monthLocked && user?.role === 'admin' && (
                        <Button variant="outline" size="sm" className="h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                            onClick={() => { setDeleteConfirmText(''); setDeleteMonthOpen(true); }} data-testid="production-delete-month-btn"
                            title="Efface toutes les quantités et numéros de BL du mois affiché (tous clients) pour pouvoir le recréer proprement">
                            <Trash2 className="w-4 h-4 mr-1" />Effacer le mois
                        </Button>
                    )}
                    {user?.role === 'admin' && (
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0"
                            onClick={() => { setTrashOpen(true); loadBackups(); }} data-testid="production-trash-btn"
                            title="Corbeille — mois supprimés récupérables pendant 30 jours">
                            <ArchiveRestore className="w-4 h-4" />
                        </Button>
                    )}
                    <Button variant="outline" size="sm" className="h-8" onClick={() => setAddProductOpen(true)} data-testid="production-lines-btn">
                        <Plus className="w-4 h-4 mr-1" />Lignes
                    </Button>
                </div>
            </div>

            {labelsView ? (
                <LabelsView clients={clients} />
            ) : printView ? (
                <PrintWeekView month={month} />
            ) : blPreviewView ? (
                <BlView month={month} clients={clients.filter(c => c.has_bl !== false)} initialDate={pdfDate} />
            ) : billingView ? (
                <BillingView month={month} />
            ) : sheetView ? (
                <DailySheetView date={pdfDate} month={month} products={products} />
            ) : (
            <Card>
                <CardContent className="p-0 overflow-x-auto">
                    <div ref={gridWrapRef} tabIndex={0} onKeyDown={onGridKeyDown} className="outline-none select-none">
                    <table className="text-xs border-collapse w-full" data-testid="production-grid">
                        <thead>
                            <tr>
                                <th className="sticky left-0 bg-slate-100 border px-2 py-1 text-left min-w-[190px] z-10">Produit</th>
                                {monthDays.map(d => (
                                    <th key={d.iso} className={`border px-0.5 py-1 text-center min-w-[30px] ${d.iso === todayIso() ? 'bg-teal-600 text-white border-teal-700' : ['S', 'D'].includes(d.letter) && [0, 6].includes(new Date(d.iso + 'T00:00:00').getDay()) ? 'bg-slate-200' : 'bg-slate-100'}`}>
                                        <div className={`text-[9px] ${d.iso === todayIso() ? 'text-teal-100' : 'text-slate-500'}`}>{d.letter}</div>
                                        <div className="font-bold">{d.num}</div>
                                    </th>
                                ))}
                                <th className="border px-1 py-1 bg-slate-200 text-center min-w-[42px]">Total</th>
                            </tr>
                            {client?.has_bl !== false && (
                            <tr>
                                <td className="sticky left-0 bg-slate-50 border px-2 py-0.5 text-[10px] font-semibold text-slate-500 z-10">N° BL</td>
                                {monthDays.map(d => (
                                    <td key={d.iso} className={`border p-0 text-center ${d.iso === todayIso() ? 'bg-teal-50 border-x-teal-300' : 'bg-slate-50'}`}>
                                        <input
                                            className="compact-cell-input w-full h-5 text-[9px] text-center bg-transparent outline-none focus:bg-amber-50"
                                            defaultValue={days[d.iso]?.bl_number ?? ''}
                                            key={`bl-${d.iso}-${days[d.iso]?.bl_number ?? ''}`}
                                            disabled={monthLocked}
                                            onBlur={async e => {
                                                const v = e.target.value.trim();
                                                const num = v === '' ? null : parseInt(v, 10);
                                                if ((days[d.iso]?.bl_number ?? null) === num) return;
                                                await productionAPI.setDayBl({ client_id: activeClient, date: d.iso, bl_number: num });
                                                setDays(p => ({ ...p, [d.iso]: { ...(p[d.iso] || { date: d.iso, quantities: {} }), bl_number: num } }));
                                            }}
                                            data-testid={`bl-input-${d.iso}`}
                                        />
                                    </td>
                                ))}
                                <td className="border bg-slate-50" />
                            </tr>
                            )}
                        </thead>
                        <tbody>
                            {rowIds.map((pid, rIdx) => {
                                const p = productMap[pid];
                                let total = 0;
                                return (
                                    <tr key={pid} className={`hover:bg-teal-50/40 ${dragOverPid === pid ? 'border-t-2 border-t-teal-500' : ''} ${dragPid === pid ? 'opacity-40' : ''}`}>
                                        <td className="sticky left-0 bg-white border px-1 py-0.5 font-medium whitespace-nowrap z-10 group" title={p.name}
                                            draggable
                                            onDragStart={e => { if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; setDragPid(pid); }}
                                            onDragOver={e => { e.preventDefault(); if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'; if (dragOverPid !== pid) setDragOverPid(pid); }}
                                            onDragLeave={() => { if (dragOverPid === pid) setDragOverPid(null); }}
                                            onDrop={e => { e.preventDefault(); moveRow(dragPid, pid); setDragPid(null); setDragOverPid(null); }}
                                            onDragEnd={() => { setDragPid(null); setDragOverPid(null); }}
                                            data-testid={`row-handle-${pid}`}>
                                            <span className="inline-flex items-center gap-1 cursor-grab active:cursor-grabbing">
                                                <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-slate-500 shrink-0" />
                                                {p.name}
                                            </span>
                                        </td>
                                        {monthDays.map((d, cIdx) => {
                                            const day = days[d.iso];
                                            const q = day?.quantities?.[pid];
                                            const corr = day?.corrections?.[pid];
                                            if (q) total += q;
                                            const isEditing = editing && editing.date === d.iso && editing.productId === pid;
                                            const selected = inSel(rIdx, cIdx);
                                            return (
                                                <td key={d.iso}
                                                    className={`border p-0 text-center cursor-cell ${selected ? 'bg-teal-200/60' : corr ? 'bg-red-50' : d.iso === todayIso() ? 'bg-teal-50/70 border-x-teal-300' : ''}`}
                                                    title={corr ? (corr.prev !== null && corr.prev !== undefined ? `Correction${corr.by ? ` (${corr.by})` : ''} — ancienne valeur : ${corr.prev}` : 'Correction importée (en rouge dans le fichier Excel)') : undefined}
                                                    onMouseDown={e => {
                                                        if (isEditing) return;
                                                        e.preventDefault();
                                                        gridWrapRef.current?.focus();
                                                        const single = selBounds && selBounds.r1 === selBounds.r2 && selBounds.c1 === selBounds.c2 && selBounds.r1 === rIdx && selBounds.c1 === cIdx;
                                                        if (single) {
                                                            if (guardLocked()) return;
                                                            setEditing({ date: d.iso, productId: pid, value: q ?? '' });
                                                        } else {
                                                            selDragRef.current = true;
                                                            setSel({ anchor: { r: rIdx, c: cIdx }, focus: { r: rIdx, c: cIdx } });
                                                        }
                                                    }}
                                                    onMouseEnter={() => { if (selDragRef.current) setSel(s => s ? { ...s, focus: { r: rIdx, c: cIdx } } : s); }}
                                                    onDoubleClick={() => !isEditing && !guardLocked() && setEditing({ date: d.iso, productId: pid, value: q ?? '' })}
                                    onContextMenu={e => {
                                        if (!corr) return;
                                        e.preventDefault();
                                        if (guardLocked()) return;
                                        setClearCorr({ date: d.iso, productId: pid });
                                    }}
                                                    data-testid={`cell-${d.iso}-${pid}`}
                                                >
                                                    {isEditing ? (
                                                        <input
                                                            autoFocus
                                                            className="w-full h-6 text-xs text-center outline-none bg-amber-50"
                                                            value={editing.value}
                                                            onChange={e => setEditing(ed => ({ ...ed, value: e.target.value }))}
                                                            onBlur={commitEdit}
                                                            onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(null); }}
                                                            data-testid="cell-edit-input"
                                                        />
                                                    ) : (
                                                        <span className={`block h-6 leading-6 ${corr ? 'text-red-600 font-bold' : ''}`}>
                                                            {q !== undefined && q !== null ? (q === Math.trunc(q) ? Math.trunc(q) : q) : ''}
                                                        </span>
                                                    )}
                                                </td>
                                            );
                                        })}
                                        <td className="border px-1 text-center font-bold bg-slate-50">{total ? (total === Math.trunc(total) ? Math.trunc(total) : Math.round(total * 100) / 100) : ''}</td>
                                    </tr>
                                );
                            })}
                            {rowIds.length === 0 && (
                                <tr><td colSpan={monthDays.length + 2} className="border px-3 py-8 text-center text-slate-400">
                                    Aucun produit pour ce client — ajoutez des lignes via « + Lignes ».
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                    </div>
                    <div className="px-3 py-1.5 text-[10px] text-slate-400 border-t">
                        Astuce : glissez pour sélectionner une plage · Ctrl+C copier · Ctrl+V coller (aussi depuis Excel) · Suppr effacer · Entrée ou double-clic pour saisir · clic droit sur une case rouge pour retirer la correction · glissez le nom d'un produit pour réordonner les lignes
                    </div>
                </CardContent>
            </Card>
            )}

            {/* Dialogue correction visible */}
            <Dialog open={!!pending} onOpenChange={o => { if (!o) setPending(null); }}>
                <DialogContent className="max-w-sm" data-testid="correction-dialog">
                    <DialogHeader><DialogTitle>Modification d'une valeur existante</DialogTitle>
                        <DialogDescription className="sr-only">Choisir si la correction est visible</DialogDescription>
                    </DialogHeader>
                    {pending && (
                        <p className="text-sm text-slate-600">
                            {productMap[pending.productId]?.name} — {pending.date.slice(8)}/{pending.date.slice(5, 7)} :{' '}
                            <b>{pending.oldQty}</b> → <b>{pending.newQty ?? 'vide'}</b>
                            <br /><br />Marquer comme <b>correction visible</b> (affichée en rouge, ancienne valeur conservée) ?
                        </p>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => { if (!pending) return; saveCell(pending.date, pending.productId, pending.newQty, false); setPending(null); }} data-testid="correction-no-btn">
                            Non, simple modif
                        </Button>
                        <Button className="bg-red-600 hover:bg-red-700" onClick={() => { if (!pending) return; saveCell(pending.date, pending.productId, pending.newQty, true); setPending(null); }} data-testid="correction-yes-btn">
                            Oui, correction visible
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={!!clearCorr} onOpenChange={o => { if (!o) setClearCorr(null); }}>
                <DialogContent className="max-w-sm" data-testid="clear-correction-dialog">
                    <DialogHeader><DialogTitle>Retirer le marquage rouge</DialogTitle>
                        <DialogDescription className="sr-only">Confirmation de suppression de la correction</DialogDescription>
                    </DialogHeader>
                    {clearCorr && (
                        <p className="text-sm text-slate-600">
                            {productMap[clearCorr.productId]?.name} — {clearCorr.date.slice(8)}/{clearCorr.date.slice(5, 7)}
                            <br /><br />La valeur est conservée, seul le marquage <b className="text-red-600">rouge</b> de correction sera retiré.
                        </p>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setClearCorr(null)} data-testid="clear-correction-cancel-btn">Annuler</Button>
                        <Button onClick={removeCorrection} data-testid="clear-correction-confirm-btn">Retirer le rouge</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={nextMonthOpen} onOpenChange={setNextMonthOpen}>
                <DialogContent className="max-w-md" data-testid="next-month-dialog">
                    <DialogHeader>
                        <DialogTitle>Recopier les données sur {nextMonthLabel}</DialogTitle>
                        <DialogDescription className="sr-only">Confirmation de création du mois suivant</DialogDescription>
                    </DialogHeader>
                    <div className="text-sm text-slate-600 space-y-2">
                        <p>Le mois <b>{nextMonthLabel}</b> sera créé pour <b>tous les clients</b> à partir des données de <b>{MONTHS_FR[Number(month.split('-')[1]) - 1]} {month.split('-')[0]}</b> :</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Quantités recopiées par jour de semaine (1er lundi → 1er lundi, etc.)</li>
                            <li>Numéros de BL pré-attribués à la suite des existants</li>
                        </ul>
                        <p className="text-xs text-slate-400">Si le mois suivant contient déjà des données, la création sera bloquée.</p>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setNextMonthOpen(false)} data-testid="next-month-cancel-btn">Annuler</Button>
                        <Button onClick={createNextMonth} disabled={creatingMonth} data-testid="next-month-confirm-btn">
                            {creatingMonth && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Créer le mois
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteMonthOpen} onOpenChange={setDeleteMonthOpen}>
                <DialogContent className="max-w-md" data-testid="delete-month-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-red-700">Effacer {MONTHS_FR[Number(month.split('-')[1]) - 1]} {month.split('-')[0]} ?</DialogTitle>
                        <DialogDescription className="sr-only">Confirmation d'effacement du mois</DialogDescription>
                    </DialogHeader>
                    <div className="text-sm text-slate-600 space-y-2">
                        <p>Toutes les données de <b>{MONTHS_FR[Number(month.split('-')[1]) - 1]} {month.split('-')[0]}</b> seront supprimées pour <b>tous les clients</b> :</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Quantités saisies (grilles)</li>
                            <li>Numéros de BL du mois</li>
                        </ul>
                        <p className="text-xs text-slate-500">Vous pourrez ensuite recréer le mois proprement via « Recopier données » depuis le mois précédent (les BL reprendront la bonne suite par client).</p>
                        <p className="text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-1.5">Une sauvegarde automatique sera conservée <b>30 jours</b> dans la corbeille et pourra être restaurée.</p>
                        <div className="pt-1">
                            <label className="text-xs font-medium text-slate-700">Pour confirmer, tapez « <b>{monthLabel}</b> » :</label>
                            <Input value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
                                placeholder={monthLabel} className="mt-1 h-9" autoFocus
                                data-testid="delete-month-confirm-input" />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setDeleteMonthOpen(false)} data-testid="delete-month-cancel-btn">Annuler</Button>
                        <Button variant="destructive" onClick={deleteMonth} disabled={deletingMonth || !deleteConfirmOk} data-testid="delete-month-confirm-btn">
                            {deletingMonth && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Effacer le mois
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
                <DialogContent className="max-w-lg" data-testid="production-trash-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><ArchiveRestore className="w-5 h-5" />Corbeille — mois supprimés</DialogTitle>
                        <DialogDescription>Les mois effacés sont récupérables pendant 30 jours.</DialogDescription>
                    </DialogHeader>
                    {trashLoading ? (
                        <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                    ) : backups.length === 0 ? (
                        <p className="text-sm text-slate-500 py-6 text-center" data-testid="trash-empty-msg">La corbeille est vide.</p>
                    ) : (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                            {backups.map(b => {
                                const [y, m] = b.month.split('-');
                                const lbl = `${MONTHS_FR[Number(m) - 1]} ${y}`;
                                const daysLeft = Math.max(0, Math.ceil((new Date(b.expires_at) - Date.now()) / 86400000));
                                return (
                                    <div key={b.id} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2" data-testid={`trash-item-${b.month}`}>
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium">{lbl}</div>
                                            <div className="text-xs text-slate-500">
                                                {b.days_count} jour(s) · effacé le {new Date(b.deleted_at).toLocaleDateString('fr-FR')} par {b.deleted_by} · expire dans {daysLeft} j
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <Button size="sm" className="h-8" onClick={() => restoreBackup(b.id)} disabled={restoringId === b.id}
                                                data-testid={`trash-restore-${b.month}`}>
                                                {restoringId === b.id && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}Restaurer
                                            </Button>
                                            <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                                                title="Supprimer définitivement" onClick={() => purgeBackup(b.id)}
                                                data-testid={`trash-purge-${b.month}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <AddProductDialog
                open={addProductOpen}
                onOpenChange={setAddProductOpen}
                products={products}
                client={client}
                checkedIds={gridProductIds}
                month={month}
                monthLabel={`${MONTHS_FR[Number(month.split('-')[1]) - 1]} ${month.split('-')[0]}`}
                onDone={async () => { await loadBase(); await loadGrid(); }}
            />
            <ManageClientsDialog
                open={manageClientsOpen}
                onOpenChange={setManageClientsOpen}
                onChanged={async () => {
                    const res = await productionAPI.clients();
                    setClients(res.data);
                    if (!res.data.find(c => c.id === activeClient)) setActiveClient(res.data[0]?.id || null);
                }}
            />
        </div>
    );
}

function BillingView({ month }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        setLoading(true);
        productionAPI.billing(month).then(r => setData(r.data))
            .catch(() => toast.error('Erreur de chargement de la facturation'))
            .finally(() => setLoading(false));
    }, [month]);
    if (loading) return <div className="py-12 text-center text-slate-400">Chargement…</div>;
    if (!data) return null;
    const eur = v => (v === null || v === undefined) ? '—' : v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
    const fmt = (v) => (v === Math.trunc(v) ? Math.trunc(v) : Math.round(v * 10) / 10);
    return (
        <div className="space-y-4" data-testid="billing-view">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h2 className="font-bold text-slate-900">Facturation — {MONTHS_FR[Number(month.split('-')[1]) - 1]} {month.split('-')[0]}</h2>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => openPdf(productionAPI.billingPdfUrl(month), 'Aucune quantité ce mois-ci')} data-testid="billing-print-btn">
                        <Printer className="w-4 h-4 mr-1.5" />PDF
                    </Button>
                    <PrintButton testId="billing-direct-print-btn" errMsg="Aucune quantité ce mois-ci"
                        getBlob={async () => (await api.get(productionAPI.billingPdfUrl(month), { responseType: 'blob' })).data} />
                    <div className="text-sm font-bold bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md px-3 py-1.5" data-testid="billing-grand-total">
                        Total mensuel : {eur(data.grand_total)} HT
                    </div>
                </div>
            </div>
            <div className="grid lg:grid-cols-3 gap-4 items-start">
                {data.clients.map(c => (
                    <Card key={c.id}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="font-bold text-sm">{c.code}</h3>
                                <span className="text-[11px] text-slate-400">Tarif : {c.pricing_client || '?'}</span>
                            </div>
                            {c.lines.length === 0 ? (
                                <p className="text-xs text-slate-400 py-4 text-center">Aucune quantité ce mois-ci.</p>
                            ) : (
                            <table className="text-xs border-collapse w-full" data-testid={`billing-table-${c.code}`}>
                                <thead><tr>
                                    <th className="border bg-slate-100 px-2 py-1 text-left">Produit</th>
                                    <th className="border bg-slate-100 px-1.5 py-1 w-12">Qté</th>
                                    <th className="border bg-slate-100 px-1.5 py-1 w-16">PU HT</th>
                                    <th className="border bg-slate-100 px-1.5 py-1 w-20">Total HT</th>
                                </tr></thead>
                                <tbody>
                                    {c.lines.map(l => (
                                        <tr key={l.name} className={l.price_ht === null ? 'bg-amber-50' : ''}>
                                            <td className="border px-2 py-0.5">{l.name}</td>
                                            <td className="border px-1.5 py-0.5 text-center">{fmt(l.qty)}</td>
                                            <td className="border px-1.5 py-0.5 text-right whitespace-nowrap">{eur(l.price_ht)}</td>
                                            <td className="border px-1.5 py-0.5 text-right font-semibold whitespace-nowrap">{eur(l.total)}</td>
                                        </tr>
                                    ))}
                                    <tr className="bg-slate-100 font-bold">
                                        <td className="border px-2 py-1" colSpan={3}>TOTAL {c.code} HT</td>
                                        <td className="border px-1.5 py-1 text-right whitespace-nowrap" data-testid={`billing-total-${c.code}`}>{eur(c.total)}</td>
                                    </tr>
                                </tbody>
                            </table>
                            )}
                            {c.missing_price.length > 0 && (
                                <p className="text-[11px] text-amber-700 mt-2" data-testid={`billing-missing-price-${c.code}`}>
                                    Tarif non renseigné ({c.missing_price.length}) : {c.missing_price.join(', ')} — à saisir dans Recettes → Tarifs & Marges ({c.pricing_client || 'client tarifaire'}).
                                </p>
                            )}
                            {c.missing_recipe.length > 0 && (
                                <p className="text-[11px] text-red-600 mt-2" data-testid={`billing-missing-recipe-${c.code}`}>
                                    Recette non liée ({c.missing_recipe.length}) : {c.missing_recipe.join(', ')} — à lier via Production → Lignes.
                                </p>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
}

function DailySheetView({ date: initialDate, month, products }) {
    const [date, setDate] = useState(initialDate);
    const [availDays, setAvailDays] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dragPid, setDragPid] = useState(null);
    const [overPid, setOverPid] = useState(null);
    const [dragCli, setDragCli] = useState(null);
    const [overCli, setOverCli] = useState(null);
    const [dragItem, setDragItem] = useState(null);
    const [overItem, setOverItem] = useState(null);
    const [editHdr, setEditHdr] = useState(null);
    const [editSep, setEditSep] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const res = await productionAPI.dailySheet(date);
            setData(res.data);
        } catch {
            setData(null);
        }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

    useEffect(() => {
        const [y, m] = month.split('-').map(Number);
        const last = new Date(y, m, 0).getDate();
        productionAPI.printAvailability(`${month}-01`, `${month}-${String(last).padStart(2, '0')}`)
            .then(res => {
                const ds = Object.keys(res.data.days || {}).sort();
                setAvailDays(ds);
                setDate(prev => (ds.length && !ds.includes(prev)) ? ds[0] : prev);
            })
            .catch(() => setAvailDays([]));
        // eslint-disable-next-line
    }, [month]);

    const fmt = (v) => (v === null || v === undefined || v === '' ? '' : (v === Math.trunc(v) ? Math.trunc(v) : Math.round(v * 10) / 10));

    const moveLine = async (fromPid, toPid) => {
        if (!fromPid || !toPid || fromPid === toPid || !data) return;
        const visible = data.lines.map(l => l.product_id);
        const fi = visible.indexOf(fromPid), ti = visible.indexOf(toPid);
        if (fi < 0 || ti < 0) return;
        const newVisible = [...visible];
        newVisible.splice(fi, 1);
        newVisible.splice(newVisible.indexOf(toPid) + (fi < ti ? 1 : 0), 0, fromPid);
        const newLines = newVisible.map(pid => data.lines.find(l => l.product_id === pid));
        setData(d => ({ ...d, lines: newLines }));
        const full = [...products].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0) || a.name.localeCompare(b.name)).map(p => p.id);
        const positions = full.map((id, i) => visible.includes(id) ? i : -1).filter(i => i >= 0);
        const fullNew = [...full];
        positions.forEach((pos, k) => { fullNew[pos] = newVisible[k]; });
        await productionAPI.reorderProducts(fullNew);
        toast.success('Ordre de la fiche mis à jour');
    };

    const keyOf = (x) => (x.type === 'sep' ? x.id : x.name);

    const moveClient = async (fromId, toId) => {
        if (!fromId || !toId || fromId === toId || !data) return;
        const ids = data.clients.map(c => c.id);
        const fi = ids.indexOf(fromId), ti = ids.indexOf(toId);
        if (fi < 0 || ti < 0) return;
        const order = [...ids];
        order.splice(fi, 1);
        order.splice(order.indexOf(toId) + (fi < ti ? 1 : 0), 0, fromId);
        const perm = order.map(id => ids.indexOf(id));
        setData(d => ({
            ...d,
            clients: perm.map(i => d.clients[i]),
            lines: d.lines.map(l => ({ ...l, qtys: perm.map(i => l.qtys[i]) })),
        }));
        await productionAPI.reorderClients(order);
        toast.success('Ordre des clients mis à jour (écran + PDF)');
    };

    const moveNamed = async (kind, from, to) => {
        if (!from || !to || from === to || !data) return;
        const list = kind === 'synth' ? data.synth : data.posts;
        const fi = list.findIndex(x => keyOf(x) === from), ti = list.findIndex(x => keyOf(x) === to);
        if (fi < 0 || ti < 0) return;
        const arr = [...list];
        const [item] = arr.splice(fi, 1);
        arr.splice(arr.findIndex(x => keyOf(x) === to) + (fi < ti ? 1 : 0), 0, item);
        setData(d => (kind === 'synth' ? { ...d, synth: arr } : { ...d, posts: arr }));
        await productionAPI.sheetOrder(kind, arr.map(keyOf));
        toast.success('Ordre mis à jour');
    };

    const moveAny = async (from, fromKey, to, toKey) => {
        if (from === to) return moveNamed(from, fromKey, toKey);
        const src = from === 'synth' ? data.synth : data.posts;
        const dst = to === 'synth' ? data.synth : data.posts;
        const item = src.find(x => keyOf(x) === fromKey);
        if (!item) return;
        const newSrc = src.filter(x => keyOf(x) !== fromKey);
        const newDst = [...dst];
        const ti = toKey ? newDst.findIndex(x => keyOf(x) === toKey) : -1;
        newDst.splice(ti < 0 ? newDst.length : ti, 0, item);
        setData(d => ({ ...d, synth: to === 'synth' ? newDst : newSrc, posts: to === 'posts' ? newDst : newSrc }));
        if (item.type !== 'sep') await productionAPI.sheetBlock(item.name, to);
        await productionAPI.sheetOrder(to, newDst.map(keyOf));
        await productionAPI.sheetOrder(from, newSrc.map(keyOf));
        toast.success('Ligne déplacée dans l\'autre bloc');
    };

    const addSep = async (kind) => {
        const list = kind === 'synth' ? data.synth : data.posts;
        const id = `#SEP#${Date.now()}#FAMILLE`;
        const arr = [...list, { type: 'sep', id, name: 'FAMILLE' }];
        setData(d => (kind === 'synth' ? { ...d, synth: arr } : { ...d, posts: arr }));
        setEditSep(id);
        await productionAPI.sheetOrder(kind, arr.map(keyOf));
    };

    const saveSep = async (kind, id, label) => {
        const v = (label || '').trim() || 'FAMILLE';
        const list = kind === 'synth' ? data.synth : data.posts;
        const newId = `#SEP#${id.split('#')[2]}#${v}`;
        const arr = list.map(x => (keyOf(x) === id ? { ...x, id: newId, name: v } : x));
        setEditSep(null);
        setData(d => (kind === 'synth' ? { ...d, synth: arr } : { ...d, posts: arr }));
        await productionAPI.sheetOrder(kind, arr.map(keyOf));
    };

    const delSep = async (kind, id) => {
        const list = kind === 'synth' ? data.synth : data.posts;
        const arr = list.filter(x => keyOf(x) !== id);
        setData(d => (kind === 'synth' ? { ...d, synth: arr } : { ...d, posts: arr }));
        await productionAPI.sheetOrder(kind, arr.map(keyOf));
        toast.success('Séparateur supprimé');
    };

    const dragProps = (kind, name) => ({
        draggable: true,
        onDragStart: (e) => { if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; setDragItem({ kind, name }); },
        onDragOver: (e) => { e.preventDefault(); setOverItem({ kind, name }); },
        onDragLeave: () => { if (overItem?.name === name && overItem?.kind === kind) setOverItem(null); },
        onDrop: (e) => {
            e.preventDefault();
            if (dragItem) moveAny(dragItem.kind, dragItem.name, kind, name);
            setDragItem(null); setOverItem(null);
        },
        onDragEnd: () => { setDragItem(null); setOverItem(null); },
    });
    const blockDropProps = (kind) => ({
        onDragOver: (e) => { if (dragItem && dragItem.kind !== kind) e.preventDefault(); },
        onDrop: (e) => {
            e.preventDefault();
            if (dragItem && dragItem.kind !== kind) moveAny(dragItem.kind, dragItem.name, kind, null);
            setDragItem(null); setOverItem(null);
        },
    });
    const dragRowCls = (kind, name) =>
        `${overItem?.kind === kind && overItem?.name === name ? 'border-t-2 border-t-teal-500' : ''} ${dragItem?.kind === kind && dragItem?.name === name ? 'opacity-40' : ''} hover:bg-teal-50/40`;

    const calendar = <DayCalendar month={month} selected={date} onSelect={setDate} enabledDays={availDays} />;

    if (loading) return (
        <div className="flex items-start gap-6 flex-wrap">
            <div className="flex-1 py-12 text-center text-slate-400">Chargement…</div>
            <div className="shrink-0">{calendar}</div>
        </div>
    );
    if (!data || !data.lines?.length) return (
        <Card><CardContent className="p-4">
            <div className="flex items-start gap-6 flex-wrap">
                <div className="flex-1 py-14 text-center text-slate-400" data-testid="sheet-empty">
                    Aucune quantité pour le {date.split('-').reverse().join('/')}.
                </div>
                <div className="shrink-0">{calendar}</div>
            </div>
        </CardContent></Card>
    );

    const totals = data.clients.map((_, ci) => data.lines.reduce((s, l) => s + (l.qtys[ci] || 0), 0));
    const grand = data.lines.reduce((s, l) => s + l.total, 0);
    const WEEKDAYS_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const weekday = WEEKDAYS_FR[new Date(date + 'T12:00:00').getDay()].toUpperCase();
    const hdrs = { synth_title: 'Synthèse préparation', synth_col: 'ÉLÉMENT À PRÉPARER', posts_title: 'Postes (nombre de pièces)', posts_col: 'POSTE', ...(data.headers || {}) };
    const saveHdr = async (key, value) => {
        const v = (value || '').trim();
        setEditHdr(null);
        if (!v || v === hdrs[key]) return;
        setData(d => ({ ...d, headers: { ...(d.headers || {}), [key]: v } }));
        await productionAPI.sheetHeaders({ [key]: v });
        toast.success('En-tête mis à jour');
    };
    const hdrEl = (key) => editHdr === key ? (
        <input autoFocus defaultValue={hdrs[key]}
            className="border border-teal-400 rounded px-1 py-0.5 text-xs font-bold bg-white w-48"
            onBlur={e => saveHdr(key, e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') saveHdr(key, e.target.value); if (e.key === 'Escape') setEditHdr(null); }}
            data-testid={`hdr-input-${key}`} />
    ) : (
        <span className="cursor-text hover:bg-amber-100/70 rounded px-0.5" title="Cliquer pour renommer"
            onClick={() => setEditHdr(key)} data-testid={`hdr-${key}`}>{hdrs[key]}</span>
    );

    const sepRow = (kind, e) => (
        <tr key={e.id} className={dragRowCls(kind, e.id)}>
            <td colSpan={2} className="border bg-slate-200 px-2 py-0.5 font-bold group cursor-grab active:cursor-grabbing" {...dragProps(kind, e.id)} data-testid={`sep-row-${e.id}`}>
                <span className="flex items-center gap-1">
                    <GripVertical className="w-3 h-3 text-slate-400 group-hover:text-slate-600 shrink-0" />
                    {editSep === e.id ? (
                        <input autoFocus defaultValue={e.name}
                            className="border border-teal-400 rounded px-1 py-0 text-xs font-bold bg-white w-40"
                            onBlur={ev => saveSep(kind, e.id, ev.target.value)}
                            onKeyDown={ev => { if (ev.key === 'Enter') saveSep(kind, e.id, ev.target.value); if (ev.key === 'Escape') setEditSep(null); }}
                            data-testid="sep-input" />
                    ) : (
                        <span className="cursor-text flex-1" title="Cliquer pour renommer" onClick={() => setEditSep(e.id)}>{e.name}</span>
                    )}
                    <button className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700" title="Supprimer le séparateur"
                        onClick={() => delSep(kind, e.id)} data-testid={`sep-del-${e.id}`}>
                        <Trash2 className="w-3 h-3" />
                    </button>
                </span>
            </td>
        </tr>
    );

    return (
        <div className="space-y-4" data-testid="daily-sheet-view">
            <Card>
                <CardContent className="p-4">
                    <div className="flex items-start gap-6 flex-wrap">
                        <div className="min-w-0 flex-1">
                            <div className="mb-3">
                                <h2 className="font-bold text-slate-900">
                                    Fiche de Production — <span className="text-teal-700 font-extrabold" data-testid="sheet-weekday">{weekday}</span> {date.split('-').reverse().join('/')}
                                </h2>
                                <p className="text-[11px] text-slate-400">Glissez une ligne pour changer l'ordre, ou un en-tête client pour réordonner les colonnes (écran + PDF) · cliquez sur un en-tête pour le renommer</p>
                            </div>
                    <table className="text-[11px] border-collapse w-full max-w-3xl" data-testid="sheet-table">
                        <thead><tr>
                            <th className="border bg-slate-100 px-1.5 py-0.5 text-left">BARQUETTES</th>
                            {data.clients.map(c => (
                                <th key={c.id} draggable
                                    className={`border bg-slate-100 px-1.5 py-0.5 w-16 cursor-grab active:cursor-grabbing select-none ${overCli === c.id ? 'border-l-2 border-l-teal-500' : ''} ${dragCli === c.id ? 'opacity-40' : ''}`}
                                    title="Glisser pour changer l'ordre des colonnes clients (écran + PDF)"
                                    onDragStart={e => { if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; setDragCli(c.id); }}
                                    onDragOver={e => { e.preventDefault(); if (overCli !== c.id) setOverCli(c.id); }}
                                    onDragLeave={() => { if (overCli === c.id) setOverCli(null); }}
                                    onDrop={e => { e.preventDefault(); moveClient(dragCli, c.id); setDragCli(null); setOverCli(null); }}
                                    onDragEnd={() => { setDragCli(null); setOverCli(null); }}
                                    data-testid={`sheet-client-col-${c.code}`}>
                                    <span className="inline-flex items-center gap-0.5"><GripVertical className="w-2.5 h-2.5 text-slate-300" />{c.code}</span>
                                </th>
                            ))}
                            <th className="border bg-slate-200 px-1.5 py-0.5 w-16">TOTAL</th>
                        </tr></thead>
                        <tbody>
                            {data.lines.map(l => (
                                <tr key={l.product_id}
                                    className={`${overPid === l.product_id ? 'border-t-2 border-t-teal-500' : ''} ${dragPid === l.product_id ? 'opacity-40' : ''} hover:bg-teal-50/40`}>
                                    <td className="border px-1 py-0.5 font-medium group cursor-grab active:cursor-grabbing" draggable
                                        onDragStart={e => { if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'; setDragPid(l.product_id); }}
                                        onDragOver={e => { e.preventDefault(); if (overPid !== l.product_id) setOverPid(l.product_id); }}
                                        onDragLeave={() => { if (overPid === l.product_id) setOverPid(null); }}
                                        onDrop={e => { e.preventDefault(); moveLine(dragPid, l.product_id); setDragPid(null); setOverPid(null); }}
                                        onDragEnd={() => { setDragPid(null); setOverPid(null); }}
                                        data-testid={`sheet-row-${l.product_id}`}>
                                        <span className="inline-flex items-center gap-1">
                                            <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-slate-500 shrink-0" />{l.name}
                                        </span>
                                    </td>
                                    {l.qtys.map((q, ci) => <td key={ci} className="border px-1.5 py-0 text-center">{q ? fmt(q) : ''}</td>)}
                                    <td className="border px-1.5 py-0 text-center font-bold bg-slate-50">{fmt(l.total)}</td>
                                </tr>
                            ))}
                            <tr className="font-bold bg-slate-100">
                                <td className="border px-2 py-1">TOTAL</td>
                                {totals.map((t, i) => <td key={i} className="border px-2 py-1 text-center">{fmt(t)}</td>)}
                                <td className="border px-2 py-1 text-center">{fmt(grand)}</td>
                            </tr>
                        </tbody>
                    </table>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                            <div className="flex gap-2">
                                <Button variant="outline" size="sm" onClick={() => openPdf(productionAPI.dailySheetPdfUrl(date), 'Aucune quantité pour cette date')} data-testid="sheet-print-btn">
                                    <Printer className="w-4 h-4 mr-1.5" />PDF
                                </Button>
                                <PrintButton testId="sheet-direct-print-btn" errMsg="Aucune quantité pour cette date"
                                    getBlob={async () => (await api.get(productionAPI.dailySheetPdfUrl(date), { responseType: 'blob' })).data} />
                            </div>
                            {calendar}
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="grid sm:grid-cols-2 gap-4">
                <Card>
                    <CardContent className="p-4" {...blockDropProps('synth')}>
                        <div className="flex items-center justify-end mb-1">
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-500" onClick={() => addSep('synth')} data-testid="add-sep-synth">
                                <Plus className="w-3 h-3 mr-0.5" />Famille
                            </Button>
                        </div>
                        <table className="text-xs border-collapse w-full" data-testid="sheet-synth-table">
                            <thead><tr>
                                <th className="border bg-slate-100 px-2 py-1 text-left">{hdrEl('synth_col')}</th>
                                <th className="border bg-slate-100 px-2 py-1 w-16">Pièces</th>
                            </tr></thead>
                            <tbody>
                                {data.synth.map(e => e.type === 'sep' ? sepRow('synth', e) : (
                                    <tr key={e.name} className={dragRowCls('synth', e.name)}>
                                        <td className="border px-2 py-0.5 group cursor-grab active:cursor-grabbing" {...dragProps('synth', e.name)} data-testid={`synth-row-${e.name}`}>
                                            <span className="inline-flex items-center gap-1">
                                                <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-slate-500 shrink-0" />{e.name}
                                            </span>
                                        </td>
                                        <td className="border px-2 py-0.5 text-center">{fmt(e.pieces)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {data.missing?.length > 0 && (
                            <p className="text-[11px] text-amber-700 mt-2">Sans recette associée : {data.missing.join(', ')}</p>
                        )}
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4" {...blockDropProps('posts')}>
                        <div className="flex items-center justify-end mb-1">
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-500" onClick={() => addSep('posts')} data-testid="add-sep-posts">
                                <Plus className="w-3 h-3 mr-0.5" />Famille
                            </Button>
                        </div>
                        <table className="text-xs border-collapse w-full" data-testid="sheet-posts-table">
                            <thead><tr>
                                <th className="border bg-slate-100 px-2 py-1 text-left">{hdrEl('posts_col')}</th>
                                <th className="border bg-slate-100 px-2 py-1 w-16">Pièces</th>
                            </tr></thead>
                            <tbody>
                                {data.posts.map(p => p.type === 'sep' ? sepRow('posts', p) : (
                                    <tr key={p.name} className={dragRowCls('posts', p.name)}>
                                        <td className="border px-2 py-0.5 group cursor-grab active:cursor-grabbing" {...dragProps('posts', p.name)} data-testid={`poste-row-${p.name}`}>
                                            <span className="inline-flex items-center gap-1 font-medium">
                                                <GripVertical className="w-3 h-3 text-slate-300 group-hover:text-slate-500 shrink-0" />{p.name}
                                            </span>
                                        </td>
                                        <td className="border px-2 py-0.5 text-center font-semibold">{fmt(p.pieces)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function AddProductDialog({ open, onOpenChange, products, client, checkedIds, month, monthLabel, onDone }) {
    const [checked, setChecked] = useState([]);
    const [search, setSearch] = useState('');
    const [newName, setNewName] = useState('');
    const [localProducts, setLocalProducts] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [linking, setLinking] = useState(null);
    const [renaming, setRenaming] = useState(null);
    const [matching, setMatching] = useState(false);

    useEffect(() => {
        if (open) {
            setChecked(checkedIds || []);
            setLocalProducts(products);
            setSearch(''); setNewName(''); setLinking(null);
            productionAPI.recipesList().then(r => setRecipes(r.data.filter(x => !x.is_sub_recipe))).catch(() => {});
            productionAPI.syncRecipes().then(async res => {
                const { created, linked } = res.data;
                if (created || linked) {
                    const p = await productionAPI.products();
                    setLocalProducts(p.data);
                    toast.success(`Recettes Concept Lab synchronisées : ${created} ligne(s) ajoutée(s)${linked ? `, ${linked} liée(s)` : ''}`);
                }
            }).catch(() => {});
        }
        // eslint-disable-next-line
    }, [open, client, products, checkedIds]);

    const runMatch = async () => {
        setMatching(true);
        try {
            const res = await productionAPI.matchRecipes();
            toast.success(`${res.data.matched} associés — ${res.data.unmatched.length} sans recette`);
            const p = await productionAPI.products();
            setLocalProducts(p.data);
        } finally { setMatching(false); }
    };

    const linkManual = async (pid, rid) => {
        await productionAPI.linkRecipe(pid, rid);
        const rec = recipes.find(r => r.id === rid);
        setLocalProducts(p => p.map(x => x.id === pid ? { ...x, recipe_id: rid, recipe_name: rec?.name } : x));
        setLinking(null);
        toast.success('Recette associée');
    };

    const deleteProduct = async (p) => {
        if (!window.confirm(`Supprimer définitivement « ${p.name} » du catalogue de production ?`)) return;
        try {
            await productionAPI.deleteProduct(p.id);
            setLocalProducts(lp => lp.filter(x => x.id !== p.id));
            setChecked(c => c.filter(id => id !== p.id));
            toast.success(`« ${p.name} » supprimé`);
        } catch (e) {
            const detail = e.response?.data?.detail || '';
            if (e.response?.status === 409) {
                if (!window.confirm(`${detail}\n\nCes données peuvent être sur d'autres mois ou d'autres clients (même si la ligne est masquée).\n\nSupprimer quand même « ${p.name} » ET effacer toutes ses quantités ?`)) return;
                try {
                    const res = await productionAPI.deleteProduct(p.id, true);
                    setLocalProducts(lp => lp.filter(x => x.id !== p.id));
                    setChecked(c => c.filter(id => id !== p.id));
                    toast.success(`« ${p.name} » supprimé — quantités effacées sur ${res.data.cleared_days} jour(s)`);
                } catch (e2) {
                    toast.error(e2.response?.data?.detail || 'Suppression impossible');
                }
            } else {
                toast.error(detail || 'Suppression impossible');
            }
        }
    };

    const renameProduct = async (pid, name) => {
        const v = (name || '').trim();
        setRenaming(null);
        if (!v) return;
        try {
            await productionAPI.renameProduct(pid, v);
            setLocalProducts(p => p.map(x => x.id === pid ? { ...x, name: v.toUpperCase() } : x));
            toast.success('Produit renommé');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Renommage impossible');
        }
    };

    const toggle = (pid) => setChecked(p => p.includes(pid) ? p.filter(x => x !== pid) : [...p, pid]);

    const createProduct = async () => {
        if (!newName.trim()) return;
        const res = await productionAPI.createProduct({ name: newName.trim() });
        setLocalProducts(p => p.some(x => x.id === res.data.id) ? p : [...p, res.data]);
        setChecked(p => p.includes(res.data.id) ? p : [...p, res.data.id]);
        setNewName('');
        toast.success(`« ${res.data.name} » créé et coché`);
    };

    const save = async () => {
        const kept = (checkedIds || []).filter(id => checked.includes(id));
        const added = checked.filter(id => !kept.includes(id));
        await productionAPI.setClientProducts(client.id, [...kept, ...added], month);
        toast.success(`Lignes de ${client.code} mises à jour à partir de ${monthLabel} (${checked.length} visibles) — mois précédents inchangés`);
        onOpenChange(false);
        onDone();
    };

    const filtered = localProducts.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="client-lines-dialog">
                <DialogHeader>
                    <DialogTitle>Lignes affichées — {client?.code} · {monthLabel}</DialogTitle>
                    <DialogDescription className="text-xs">
                        Cochez les produits visibles dans la grille de {client?.label}. Les changements s'appliquent à {monthLabel} et aux mois suivants — jamais aux mois précédents. Décocher masque la ligne sans effacer les quantités.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex items-center gap-2">
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…" className="h-8" data-testid="lines-search-input" />
                    <Button variant="outline" size="sm" className="h-8 whitespace-nowrap" onClick={runMatch} disabled={matching} data-testid="lines-match-btn">
                        {matching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Assoc. auto'}
                    </Button>
                </div>
                <div className="max-h-72 overflow-y-auto border rounded-md divide-y">
                    {filtered.map(p => (
                        <div key={p.id} className="px-3 py-1.5 hover:bg-slate-50">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" checked={checked.includes(p.id)} onChange={() => toggle(p.id)}
                                    className="accent-teal-600" data-testid={`line-check-${p.id}`} />
                                {renaming === p.id ? (
                                    <input autoFocus defaultValue={p.name}
                                        className="flex-1 border border-teal-400 rounded px-1 py-0.5 text-sm"
                                        onClick={e => e.preventDefault()}
                                        onBlur={e => renameProduct(p.id, e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') renameProduct(p.id, e.target.value); if (e.key === 'Escape') setRenaming(null); }}
                                        data-testid={`line-rename-input-${p.id}`} />
                                ) : (
                                    <span className={`flex-1 ${checked.includes(p.id) ? 'font-medium' : 'text-slate-500'}`}>{p.name}</span>
                                )}
                                <button type="button"
                                    onClick={e => { e.preventDefault(); setRenaming(renaming === p.id ? null : p.id); }}
                                    className="text-slate-300 hover:text-blue-600 shrink-0"
                                    title="Renommer le produit"
                                    data-testid={`line-rename-${p.id}`}>
                                    <Pencil className="w-3.5 h-3.5" />
                                </button>
                                {p.recipe_name ? (
                                    <span className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 truncate max-w-[150px]" title={`Recette : ${p.recipe_name}`}>
                                        {p.recipe_name}
                                    </span>
                                ) : (
                                    <button type="button"
                                        onClick={e => { e.preventDefault(); setLinking(linking === p.id ? null : p.id); }}
                                        className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5"
                                        title="Aucune recette reconnue — cliquer pour associer"
                                        data-testid={`line-norecipe-${p.id}`}>
                                        ⚠ sans recette
                                    </button>
                                )}
                                    <button type="button"
                                        onClick={e => { e.preventDefault(); deleteProduct(p); }}
                                        className="text-slate-300 hover:text-red-500 shrink-0"
                                        title="Supprimer du catalogue (impossible si des quantités existent)"
                                        data-testid={`line-delete-${p.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                            </label>
                            {linking === p.id && (
                                <select className="mt-1 w-full text-xs border rounded p-1" defaultValue=""
                                    onChange={e => e.target.value && linkManual(p.id, e.target.value)}
                                    data-testid={`line-link-select-${p.id}`}>
                                    <option value="" disabled>Associer à une recette…</option>
                                    {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </select>
                            )}
                        </div>
                    ))}
                    {filtered.length === 0 && <div className="px-3 py-4 text-center text-sm text-slate-400">Aucun produit</div>}
                </div>
                <div className="flex gap-2">
                    <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Créer un nouveau produit…"
                        className="h-8" onKeyDown={e => e.key === 'Enter' && createProduct()} data-testid="lines-new-product-input" />
                    <Button variant="outline" size="sm" onClick={createProduct} data-testid="lines-create-btn"><Plus className="w-4 h-4" /></Button>
                </div>
                <DialogFooter>
                    <Button onClick={save} data-testid="lines-save-btn">Enregistrer ({checked.length} lignes)</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
